<template>
  <div class="paginated-table">
    <!-- Filters Section -->
    <div class="filters-section" v-if="showFilters">
      <el-card class="filter-card">
        <template #header>
          <div class="filter-header">
            <span>Filtros</span>
            <el-button size="small" @click="resetFilters" type="info">Limpar Filtros</el-button>
          </div>
        </template>
        
        <el-row :gutter="16">
          <!-- Dynamic Filters -->
          <slot name="filters" :filters="localFilters" :updateFilter="updateFilter"></slot>
          
          <!-- Date Range Filters -->
          <el-col :span="8" v-if="showDateFilters">
            <el-form-item label="Período">
              <el-date-picker
                v-model="dateRange"
                type="daterange"
                range-separator="até"
                start-placeholder="Data inicial"
                end-placeholder="Data final"
                format="DD/MM/YYYY"
                value-format="YYYY-MM-DD"
                @change="updateDateRange"
                clearable
              />
            </el-form-item>
          </el-col>
        </el-row>
      </el-card>
    </div>

    <!-- Results Summary -->
    <div class="results-summary" v-if="paginationData.total > 0">
      <el-tag type="info">
        Exibindo {{ ((paginationData.page - 1) * paginationData.limit) + 1 }} - 
        {{ Math.min(paginationData.page * paginationData.limit, paginationData.total) }} 
        de {{ paginationData.total }} resultados
      </el-tag>
    </div>

    <!-- Table -->
    <el-table
      :data="tableData"
      :loading="loading"
      style="width: 100%; margin-top: 16px;"
      v-bind="$attrs"
    >
      <slot></slot>
    </el-table>

    <!-- Pagination -->
    <div class="pagination-container" v-if="paginationData.totalPages > 1">
      <el-pagination
        v-model:current-page="localFilters.page"
        v-model:page-size="localFilters.limit"
        :page-sizes="[10, 25, 50, 100]"
        :total="paginationData.total"
        layout="total, sizes, prev, pager, next, jumper"
        @size-change="handleSizeChange"
        @current-change="handleCurrentChange"
      />
    </div>

    <!-- Empty State -->
    <div class="empty-state" v-if="!loading && tableData.length === 0">
      <el-empty description="Nenhum resultado encontrado">
        <el-button type="primary" @click="resetFilters" v-if="hasActiveFilters">
          Limpar Filtros
        </el-button>
      </el-empty>
    </div>
  </div>
</template>

<script setup>
import { ref, reactive, computed, watch, onMounted, defineProps, defineEmits, defineExpose } from 'vue';

const props = defineProps({
  apiEndpoint: {
    type: String,
    required: true,
  },
  initialFilters: {
    type: Object,
    default: () => ({}),
  },
  showFilters: {
    type: Boolean,
    default: true,
  },
  showDateFilters: {
    type: Boolean,
    default: false,
  },
  dateFilterFields: {
    type: Array,
    default: () => ['criadoEmInicio', 'criadoEmFim'],
  },
});

const emit = defineEmits(['data-loaded', 'filters-changed']);

// Reactive data
const loading = ref(false);
const tableData = ref([]);
const dateRange = ref(null);

const paginationData = reactive({
  total: 0,
  page: 1,
  limit: 50,
  totalPages: 0,
  hasNextPage: false,
  hasPrevPage: false,
});

const localFilters = reactive({
  page: 1,
  limit: 50,
  sortBy: '',
  sortOrder: 'DESC',
  ...props.initialFilters,
});

// Computed
const hasActiveFilters = computed(() => {
  return Object.keys(localFilters).some(key => {
    if (key === 'page' || key === 'limit' || key === 'sortBy' || key === 'sortOrder') {
      return false;
    }
    return localFilters[key] && localFilters[key] !== '';
  });
});

// Methods
const fetchData = async () => {
  loading.value = true;
  try {
    const { default: apiClient } = await import('../plugins/axios.js');
    
    // Clean up undefined/null values from filters
    const cleanFilters = Object.fromEntries(
      Object.entries(localFilters).filter(([, value]) => 
        value !== null && value !== undefined && value !== ''
      )
    );

    const { data } = await apiClient.get(props.apiEndpoint, {
      params: cleanFilters,
    });

    if (data.data) {
      // Paginated response
      tableData.value = data.data;
      Object.assign(paginationData, {
        total: data.total,
        page: data.page,
        limit: data.limit,
        totalPages: data.totalPages,
        hasNextPage: data.hasNextPage,
        hasPrevPage: data.hasPrevPage,
      });
    } else {
      // Legacy non-paginated response
      tableData.value = data;
      paginationData.total = data.length;
      paginationData.totalPages = 1;
    }

    emit('data-loaded', { data: tableData.value, pagination: paginationData });
  } catch (error) {
    console.error('Error fetching data:', error);
    tableData.value = [];
    paginationData.total = 0;
  } finally {
    loading.value = false;
  }
};

const updateFilter = (key, value) => {
  localFilters[key] = value;
  localFilters.page = 1; // Reset to first page when filtering
  fetchData();
};

const updateDateRange = (value) => {
  if (value && value.length === 2) {
    localFilters[props.dateFilterFields[0]] = value[0];
    localFilters[props.dateFilterFields[1]] = value[1];
  } else {
    localFilters[props.dateFilterFields[0]] = '';
    localFilters[props.dateFilterFields[1]] = '';
  }
  localFilters.page = 1;
  fetchData();
};

const resetFilters = () => {
  // Reset all filters except pagination
  Object.keys(localFilters).forEach(key => {
    if (key !== 'page' && key !== 'limit' && key !== 'sortBy' && key !== 'sortOrder') {
      localFilters[key] = '';
    }
  });
  localFilters.page = 1;
  dateRange.value = null;
  fetchData();
};

const handleSizeChange = (newSize) => {
  localFilters.limit = newSize;
  localFilters.page = 1;
  fetchData();
};

const handleCurrentChange = (newPage) => {
  localFilters.page = newPage;
  fetchData();
};

const refresh = () => {
  fetchData();
};

// Watch for external filter changes
watch(() => props.initialFilters, (newFilters) => {
  Object.assign(localFilters, newFilters);
  fetchData();
}, { deep: true });

// Expose methods to parent component
defineExpose({
  refresh,
  resetFilters,
  updateFilter,
});

// Initialize
onMounted(() => {
  fetchData();
});
</script>

<style scoped>
.paginated-table {
  width: 100%;
}

.filters-section {
  margin-bottom: 16px;
}

.filter-card {
  margin-bottom: 16px;
}

.filter-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.results-summary {
  margin-bottom: 8px;
}

.pagination-container {
  display: flex;
  justify-content: center;
  margin-top: 16px;
}

.empty-state {
  margin-top: 32px;
  text-align: center;
}

:deep(.el-form-item) {
  margin-bottom: 16px;
}

:deep(.el-form-item__label) {
  font-weight: 500;
}
</style>